import React, { useEffect, useRef, useState } from 'react';
import { Alert } from '@mui/material';
import { PaymentElement, useCheckoutElements } from '@stripe/react-stripe-js/checkout';
import type { PromoSyncState, StripeCheckout } from '../checkout.types';
import { checkoutSignature } from '../checkout.helpers';
import { PaymentFormSkeleton } from './CheckoutSkeletons';

/** The promotion code currently applied to a session (from `discountAmounts`). */
const currentPromoOf = (checkout: StripeCheckout): string =>
  checkout.discountAmounts?.find((d) => d.promotionCode)?.promotionCode ?? '';

// Safety net: if the element never signals ready/error (e.g. a stuck load), open
// the promo gate anyway after this long so a link-borne code still applies.
const PROMO_GATE_FALLBACK_MS = 8000;

/**
 * Per-interval payment field. One is mounted per interval (the off-screen one
 * stays warm so its card input survives an interval toggle). Only the visible
 * instance reports its session up to the page, syncs the email, and applies the
 * promo code — so prices, terms, captcha and submit can live at page level,
 * stable across toggles.
 */
const CheckoutPaymentElement: React.FC<{
  visible: boolean;
  email: string;
  canUpdateEmail: boolean;
  /** Desired promo code for this session; empty string = none applied. */
  promoCode: string;
  onReport: (checkout: StripeCheckout | null) => void;
  /** Reports the in-place promo apply/remove lifecycle up to the page. */
  onPromoSync: (state: PromoSyncState) => void;
}> = ({ visible, email, canUpdateEmail, promoCode, onReport, onPromoSync }) => {
  const checkoutState = useCheckoutElements();
  const checkout = checkoutState.type === 'success' ? checkoutState.checkout : null;

  // `useCheckoutElements()` returns a fresh object each render. Keep the live
  // one in a ref and drive effects off primitive fields only, so they don't
  // re-run every render (which would thrash state updates and freeze the page).
  const checkoutRef = useRef<StripeCheckout | null>(checkout);
  checkoutRef.current = checkout;

  const checkoutId = checkout?.id ?? null;
  const checkoutEmail = checkout?.email ?? null;
  // Change-signature over id + confirmability + all rendered price fields. A
  // promo applied/removed in place keeps the same id, so this is what makes the
  // new prices propagate up (see `checkoutSignature`).
  const signature = checkout ? checkoutSignature(checkout) : null;

  // Last promo target we attempted on this session — dedupes re-renders and
  // stops an invalid code (which never lands in `discountAmounts`) from being
  // re-applied on every render.
  const lastPromoAttemptRef = useRef<string | null>(null);

  // Applying a promo in the first moments after the session reports `success`
  // makes Stripe's `applyPromotionCode` hang ~16s (the session/customer is still
  // settling server-side); the same call is <1s once the payment element is
  // ready. So gate the in-place apply on element readiness — set true on the
  // element's `onReady`, or on load error / a fallback timer so a link-borne
  // code still applies if `onReady` never fires.
  const [elementReady, setElementReady] = useState(false);

  useEffect(() => {
    if (!visible) return;
    onReport(checkoutRef.current);
  }, [visible, signature, onReport]);

  useEffect(() => {
    if (!visible || !checkoutId || elementReady) return;
    const timer = window.setTimeout(() => setElementReady(true), PROMO_GATE_FALLBACK_MS);
    return () => window.clearTimeout(timer);
  }, [visible, checkoutId, elementReady]);

  useEffect(() => {
    const current = checkoutRef.current;
    if (!visible || !current || !canUpdateEmail || !email) return;
    if (checkoutEmail === email) return;
    let cancelled = false;
    (async () => {
      const result = await current.updateEmail(email);
      if (cancelled) return;
      if (result.type === 'error') {
        // eslint-disable-next-line no-console
        console.warn('Stripe rejected email at updateEmail():', result.error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visible, checkoutId, checkoutEmail, canUpdateEmail, email]);

  // Apply / remove the promo code in place on the live session. Updating the
  // session this way (instead of recreating it) keeps the <PaymentElement>
  // mounted, so any card details the user already typed survive a promo change.
  const desiredPromo = promoCode.trim();
  useEffect(() => {
    if (!visible) return;
    const current = checkoutRef.current;
    if (!current) return;

    const inDesiredState = desiredPromo.toLowerCase() === currentPromoOf(current).toLowerCase();

    // Wait until the session has settled (element ready) before the first apply —
    // an apply fired too early hangs ~16s. Later toggles pass this immediately.
    // While gated with a change still pending, surface the applying state now so
    // the price shows a loading treatment instead of a stale (pre-promo) total.
    if (!elementReady) {
      onPromoSync({ applying: !inDesiredState });
      return;
    }

    // Already in the desired state — nothing to do; keep the attempt ref in sync.
    if (inDesiredState) {
      lastPromoAttemptRef.current = desiredPromo;
      return;
    }
    if (lastPromoAttemptRef.current === desiredPromo) return;
    lastPromoAttemptRef.current = desiredPromo;

    let cancelled = false;
    onPromoSync({ applying: true });
    (async () => {
      try {
        const result = desiredPromo
          ? await current.applyPromotionCode(desiredPromo)
          : await current.removePromotionCode();
        if (cancelled) return;
        if (result.type === 'error') {
          const invalid = 'code' in result.error && result.error.code === 'invalidCode';
          onPromoSync({ applying: false, error: invalid ? 'invalid' : 'network' });
          return;
        }
        onPromoSync({ applying: false });
      } catch {
        // Reached e.g. when the session was created without
        // `allow_promotion_codes` — Stripe throws an IntegrationError here.
        if (cancelled) return;
        onPromoSync({ applying: false, error: 'network' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visible, desiredPromo, checkoutId, onPromoSync, elementReady]);

  if (checkoutState.type === 'loading') {
    return <PaymentFormSkeleton />;
  }

  if (checkoutState.type === 'error') {
    return (
      <Alert severity='error' data-testid='stripe-checkout-elements-error'>
        {checkoutState.error.message}
      </Alert>
    );
  }

  return (
    <PaymentElement
      onReady={() => setElementReady(true)}
      onLoadError={() => setElementReady(true)}
      options={{
        fields: {
          billingDetails: {
            address: { country: 'never' },
          },
        },
      }}
    />
  );
};

export default CheckoutPaymentElement;
